import React, {useState, useEffect} from 'react';
import {App, Button, Col, Divider, Form, Input, Modal, Radio, Row, Select, Space, Table, Spin, DatePicker, Alert} from 'antd';
import {postRequest, getRequest} from "../../../api/httpRequest";
import {
    BankOutlined,
    BookOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    HomeOutlined,
    IdcardOutlined,
    SearchOutlined
} from '@ant-design/icons';

const {Option} = Select;
const {TextArea} = Input;

// Интерфейс для данных формы
interface RegistrationFormData {
    country: string;
    region: string;
    city: string;
    institutionType: string;
    schoolNumber: string; // Новое поле: номер школы
    schoolName: string;   // Новое поле: название школы
    className: string;
    classProfile: string;
    passportSeries: string;
    passportNumber: string;
    passportIssuedBy: string;
    passportIssueDate: string;
    passportDivisionCode: string;
    birthDate: string;
    birthPlace: string;
    venue: string;
    notes?: string;
}

// Интерфейс для места проведения (обновлен)
interface Venue {
    id: string;
    city: string;
    university: string;
    address: string;
    abbreviation?: string;
    name?: string;
    key?: string;
}

interface RegistrationModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: (olympiadId: number) => void;
    olympiad: any;
    gradeColors: Record<string, string>;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onSuccess,
                                                                 olympiad,
                                                                 gradeColors
                                                             }) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedVenue, setSelectedVenue] = useState<string>('');
    const [searchCity, setSearchCity] = useState('');
    const [searchAbbreviation, setSearchAbbreviation] = useState('');
    const [searchName, setSearchName] = useState('');
    const [venues, setVenues] = useState<Venue[]>([]);
    const [loadingVenues, setLoadingVenues] = useState(false);
    const [venueValidationError, setVenueValidationError] = useState<string>('');
    const [userClass, setUserClass] = useState<string>('');
    const [classOptions, setClassOptions] = useState<{value: string, label: string}[]>([]);
    const [classMismatchError, setClassMismatchError] = useState<string>('');
    const {notification} = App.useApp();

    // Функция для парсинга классов олимпиады из формата "9-11" или "10,11"
    const parseOlympiadGrades = (gradesStr: string): number[] => {
        if (!gradesStr) return [];
        
        // Убираем пробелы
        const cleanStr = gradesStr.replace(/\s/g, '');
        
        // Проверяем формат "9-11" (диапазон)
        if (cleanStr.includes('-')) {
            const [start, end] = cleanStr.split('-').map(Number);
            const result = [];
            for (let i = start; i <= end; i++) {
                result.push(i);
            }
            return result;
        }
        
        // Проверяем формат "10,11" (список)
        if (cleanStr.includes(',')) {
            return cleanStr.split(',').map(Number);
        }
        
        // Единичное значение
        return [parseInt(cleanStr)];
    };

    // Инициализация данных при открытии модального окна
    useEffect(() => {
        if (visible && olympiad) {
            loadVenues();
            setVenueValidationError('');
            setSelectedVenue('');
            setClassMismatchError('');
            
            // Парсим классы олимпиады
            const olympiadGrades = parseOlympiadGrades(olympiad.grades || '');
            
            // Определяем доступные классы для выбора
            const availableGrades = olympiadGrades.map(grade => ({
                value: grade.toString(),
                label: `${grade} класс`
            }));
            setClassOptions(availableGrades);
            
            // Если есть только один допустимый класс, предзаполняем его
            if (availableGrades.length === 1) {
                form.setFieldValue('className', availableGrades[0].value);
                setUserClass(availableGrades[0].value);
            } else {
                // Если несколько классов, сбрасываем значение
                form.setFieldValue('className', '');
            }
        }
    }, [visible, olympiad]);

    const loadVenues = async () => {
        setLoadingVenues(true);
        try {
            const response = await getRequest('/server/olympiad/place/list');
            const venuesData: Venue[] = response.data.map((venue: any) => ({
                ...venue,
                key: venue.id,
                abbreviation: venue.university,
                name: venue.university
            }));
            setVenues(venuesData);
        } catch (error) {
            notification.error({
                message: 'Ошибка загрузки мест проведения',
                description: 'Не удалось загрузить список мест проведения. Пожалуйста, попробуйте позже.',
                duration: 3
            });
            console.error('Error loading venues:', error);
        } finally {
            setLoadingVenues(false);
        }
    };

    // Фильтрация мест проведения
    const filteredVenues = venues.filter(venue => {
        return venue.city.toLowerCase().includes(searchCity.toLowerCase()) &&
            venue.university.toLowerCase().includes(searchAbbreviation.toLowerCase()) &&
            venue.university.toLowerCase().includes(searchName.toLowerCase());
    });

    // Функция для выбора площадки из таблицы
    const handleVenueSelect = (venueId: string) => {
        setSelectedVenue(venueId);
        form.setFieldValue('venue', venueId);
        setVenueValidationError('');
    };

    // Функция для проверки соответствия класса
    const validateUserClass = (selectedClass: string): boolean => {
        if (!olympiad || !olympiad.grades) return true;
        
        const olympiadGrades = parseOlympiadGrades(olympiad.grades);
        const selectedClassNum = parseInt(selectedClass);
        
        return olympiadGrades.includes(selectedClassNum);
    };

    // Функция для отправки формы
    const handleFormSubmit = async (values: RegistrationFormData) => {
        // Проверяем, выбрана ли площадка
        if (!selectedVenue) {
            setVenueValidationError('Пожалуйста, выберите площадку для участия в олимпиаде');
            notification.warning({
                message: 'Площадка не выбрана',
                description: 'Для регистрации необходимо выбрать площадку из списка',
                duration: 3
            });
            return;
        }

        // Проверяем соответствие класса
        if (!values.className) {
            setClassMismatchError('Пожалуйста, выберите класс');
            notification.warning({
                message: 'Класс не выбран',
                description: 'Для регистрации необходимо выбрать класс',
                duration: 3
            });
            return;
        }

        if (!validateUserClass(values.className)) {
            const olympiadGrades = parseOlympiadGrades(olympiad.grades);
            const gradeLabels = olympiadGrades.map(g => `${g} класс`).join(', ');
            
            setClassMismatchError(`Выбранный класс не соответствует олимпиаде. Олимпиада доступна для: ${gradeLabels}`);
            notification.error({
                message: 'Несоответствие класса',
                description: `Олимпиада "${olympiad.subject}" доступна только для ${gradeLabels}`,
                duration: 4
            });
            return;
        }

        setIsSubmitting(true);

        postRequest('/server/olympiad/register', {
            // Данные олимпиады
            olympiadId: olympiad?.id,
            placeId: values.venue,
            
            // Данные формы
            country: values.country,
            region: values.region,
            city: values.city,
            institutionType: values.institutionType,
            schoolNumber: values.schoolNumber, // Добавлено: номер школы
            schoolName: values.schoolName,     // Добавлено: название школы
            className: values.className,
            classProfile: values.classProfile,
            
            // Паспортные данные
            passportSeries: values.passportSeries,
            passportNumber: values.passportNumber,
            passportIssuedBy: values.passportIssuedBy,
            passportIssueDate: values.passportIssueDate,
            passportDivisionCode: values.passportDivisionCode,
            birthDate: values.birthDate,
            birthPlace: values.birthPlace,
        
            // Текущая дата регистрации
            registrationDate: new Date().toISOString()

        }).then(response => {
            notification.success({
                message: `Вы успешно записаны на олимпиаду!`,
                description: `Теперь вы можете перейти к выполнению заданий по ${olympiad?.subject}.`,
                duration: 4
            });
            
            // Вызываем onSuccess с ID олимпиады перед закрытием модального окна
            if (olympiad?.id) {
                onSuccess(olympiad.id);
            }
            
            onClose();
            form.resetFields();
            setSelectedVenue('');
            setVenueValidationError('');
            setClassMismatchError('');
        }).catch(error => {
            setIsSubmitting(false);
            notification.error({
                message: `Ошибка при записи на олимпиаду.`,
                description: error.message || 'Пожалуйста, попробуйте снова',
                duration: 3
            });
        });
    };

    // Колонки таблицы мест проведения
    const venueColumns = [
        {
            title: 'Город',
            dataIndex: 'city',
            key: 'city',
            width: '20%',
            render: (text: string) => <div style={{wordWrap: 'break-word', wordBreak: 'break-word'}}>{text}</div>
        },
        {
            title: 'ВУЗ',
            dataIndex: 'university',
            key: 'university',
            width: '20%',
            render: (text: string) => <div style={{wordWrap: 'break-word', wordBreak: 'break-word'}}>{text}</div>
        },
        {
            title: 'Адрес',
            dataIndex: 'address',
            key: 'address',
            width: '60%',
            render: (text: string) => <div style={{wordWrap: 'break-word', wordBreak: 'break-word'}}>{text}</div>
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <BookOutlined
                        style={{color: olympiad ? gradeColors[olympiad.grades] : '#1890ff'}}/>
                    <span>Запись на олимпиаду по {olympiad?.subject}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            centered
            className="registration-modal"
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                className="registration-form"
                scrollToFirstError
                initialValues={{ venue: '' }}
            >
                {/* Образовательное учреждение */}
                <Divider orientation="left" plain>
                    <BankOutlined/> Образовательное учреждение
                </Divider>

                <Form.Item
                    name="country"
                    label="Страна"
                    rules={[{required: true, message: 'Пожалуйста, выберите страну'}]}
                >
                    <Select placeholder="Выберите страну">
                        <Option value="Россия">Россия</Option>
                        <Option value="Казахстан">Казахстан</Option>
                        <Option value="Беларусь">Беларусь</Option>
                        <Option value="другая">Другая страна</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="region"
                    label="Регион (область, край, республика)"
                    rules={[{required: true, message: 'Пожалуйста, введите регион'}]}
                >
                    <Input
                        prefix={<EnvironmentOutlined/>}
                        placeholder="Московская область"
                    />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="Населенный пункт"
                    rules={[{required: true, message: 'Пожалуйста, введите населенный пункт'}]}
                >
                    <Input
                        prefix={<HomeOutlined/>}
                        placeholder="г. Москва"
                    />
                </Form.Item>

                <Form.Item
                    name="institutionType"
                    label="Вид учреждения"
                    rules={[{required: true, message: 'Пожалуйста, выберите вид учреждения'}]}
                >
                    <Select placeholder="Выберите вид учреждения">
                        <Option value="Ж.д. школа">Ж.д. школа</Option>
                        <Option value="вечерняя сменная школа(ШРМ)">вечерняя сменная школа(ШРМ)</Option>
                        <Option value="среднее специальное учебное заведение (ССУЗ)">среднее специальное учебное заведение (ССУЗ)</Option>
                        <Option value="школа-интернат">школа-интернат</Option>
                        <Option value="ФМШ">ФМШ</Option>
                        <Option value="гимназия МИИТа">гимназия МИИТа</Option>
                        <Option value="ВУЗ">ВУЗ</Option>
                        <Option value="лицей">лицей</Option>
                        <Option value="гимназия">гимназия</Option>
                        <Option value="коррекционная школа">коррекционная школа</Option>
                        <Option value="кадетская школа-интернат">кадетская школа-интернат</Option>
                        <Option value="кадетская школа">кадетская школа</Option>
                        <Option value="общеобразовательная школа">общеобразовательная школа</Option>
                        <Option value="специальное УВУ для обучающихся с девиантным (общественно-опасным) поведением)">специальное УВУ для обучающихся с девиантным (общественно-опасным) поведением)</Option>
                    </Select>
                </Form.Item>

                {/* Новые поля: номер школы и название школы */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="schoolNumber"
                            label="Номер школы"
                            rules={[
                                {required: true, message: 'Пожалуйста, введите номер школы'},
                                {pattern: /^[0-9]+$/, message: 'Номер школы должен содержать только цифры'}
                            ]}
                        >
                            <Input 
                                placeholder="Например: 123" 
                                maxLength={10}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="schoolName"
                            label="Название школы"
                            rules={[{required: true, message: 'Пожалуйста, введите название школы'}]}
                        >
                            <Input 
                                placeholder="Например: Средняя общеобразовательная школа" 
                                maxLength={100}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Поле класса с предзаполнением из олимпиады */}
                <Form.Item
                    name="className"
                    label={
                        <span>
                            Класс
                            {olympiad?.grades && (
                                <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: '8px' }}>
                                    (Олимпиада доступна для {olympiad.grades})
                                </span>
                            )}
                        </span>
                    }
                    rules={[
                        {required: true, message: 'Пожалуйста, выберите класс'},
                        {
                            validator: (_, value) => {
                                if (value && olympiad?.grades) {
                                    const olympiadGrades = parseOlympiadGrades(olympiad.grades);
                                    const selectedClassNum = parseInt(value);
                                    
                                    if (!olympiadGrades.includes(selectedClassNum)) {
                                        const gradeLabels = olympiadGrades.map(g => `${g} класс`).join(', ');
                                        return Promise.reject(new Error(`Олимпиада доступна только для: ${gradeLabels}`));
                                    }
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    help={classMismatchError}
                    validateStatus={classMismatchError ? 'error' : ''}
                >
                    <Select 
                        placeholder="Выберите класс"
                        onChange={(value) => {
                            setUserClass(value);
                            setClassMismatchError('');
                        }}
                    >
                        {classOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="classProfile"
                    label="Профиль класса"
                >
                    <Select placeholder="Выберите профиль">
                        <Option value="инженерный">инженерный</Option>
                        <Option value="it-класс">it-класс</Option>
                        <Option value="it-вертикаль">it-вертикаль</Option>
                        <Option value="математическая вертикаль">математическая вертикаль</Option>
                        <Option value="иное">иное</Option>
                    </Select>
                </Form.Item>

                {/* Выбор места проведения */}
                <Divider orientation="left" plain>
                    <EnvironmentOutlined/> Выбор места проведения
                </Divider>

                <Form.Item
                    name="venue"
                    label="Место проведения олимпиады"
                    required
                    help={venueValidationError}
                    validateStatus={venueValidationError ? 'error' : ''}
                >
                    <div>
                        <div style={{marginBottom: 16}}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Input
                                        placeholder="Город"
                                        value={searchCity}
                                        onChange={(e) => setSearchCity(e.target.value)}
                                        prefix={<SearchOutlined/>}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Input
                                        placeholder="ВУЗ"
                                        value={searchAbbreviation}
                                        onChange={(e) => setSearchAbbreviation(e.target.value)}
                                        prefix={<SearchOutlined/>}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Input
                                        placeholder="Наименование ВУЗа"
                                        value={searchName}
                                        onChange={(e) => setSearchName(e.target.value)}
                                        prefix={<SearchOutlined/>}
                                    />
                                </Col>
                            </Row>
                        </div>

                        {/* Сообщение о необходимости выбора площадки */}
                        {venueValidationError && (
                            <Alert
                                message={venueValidationError}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <div style={{maxHeight: 300, overflowY: 'auto', border: venueValidationError ? '1px solid #ff4d4f' : '1px solid #d9d9d9', borderRadius: '6px'}}>
                            {loadingVenues ? (
                                <div style={{textAlign: 'center', padding: '20px'}}>
                                    <Spin size="large" />
                                    <div style={{marginTop: 10}}>Загрузка мест проведения...</div>
                                </div>
                            ) : (
                                <Table
                                    dataSource={filteredVenues}
                                    columns={venueColumns}
                                    pagination={false}
                                    size="small"
                                    rowKey="id"
                                    scroll={{ x: true }}
                                    style={{width: '100%'}}
                                    onRow={(record) => ({
                                        onClick: () => {
                                            handleVenueSelect(record.id);
                                        },
                                    })}
                                    rowClassName={(record) =>
                                        selectedVenue === record.id ? 'ant-table-row-selected' : ''
                                    }
                                    locale={{
                                        emptyText: 'Нет доступных мест проведения'
                                    }}
                                />
                            )}
                        </div>
                        
                        {/* Подсказка о необходимости выбора */}
                        <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                            Нажмите на строку в таблице, чтобы выбрать площадку для участия
                        </div>
                    </div>
                </Form.Item>

                {/* Паспортные данные */}
                <Divider orientation="left" plain>
                    <IdcardOutlined/> Паспортные данные
                </Divider>

                <Form.Item
                    name="birthPlace"
                    label="Место рождения"
                    rules={[{required: true, message: 'Пожалуйста, введите место рождения'}]}
                >
                    <Input placeholder="г. Москва"/>
                </Form.Item>

                <Form.Item
                    name="passportSeries"
                    label="Серия паспорта"
                    rules={[
                        {required: true, message: 'Пожалуйста, введите серию паспорта'},
                        {pattern: /^\d{4}$/, message: 'Серия должна состоять из 4 цифр'}
                    ]}
                >
                    <Input placeholder="1234" maxLength={4}/>
                </Form.Item>

                <Form.Item
                    name="passportNumber"
                    label="Номер паспорта"
                    rules={[
                        {required: true, message: 'Пожалуйста, введите номер паспорта'},
                        {pattern: /^\d{6}$/, message: 'Номер должен состоять из 6 цифр'}
                    ]}
                >
                    <Input placeholder="123456" maxLength={6}/>
                </Form.Item>

                <Form.Item
                    name="passportIssuedBy"
                    label="Кем выдан"
                    rules={[{required: true, message: 'Пожалуйста, введите кем выдан паспорт'}]}
                >
                    <Input placeholder="ОУФМС России по г. Москве"/>
                </Form.Item>

                <Form.Item
                    name="passportIssueDate"
                    label="Дата выдачи"
                    rules={[{required: true, message: 'Пожалуйста, выберите дату выдачи'}]}
                >
                    <DatePicker
                        style={{width: '100%'}}
                        placeholder="Выберите дату выдачи"
                        format="DD.MM.YYYY"
                    />
                </Form.Item>

                <Form.Item
                    name="passportDivisionCode"
                    label="Код подразделения"
                    rules={[
                        {required: true, message: 'Пожалуйста, введите код подразделения'},
                        {pattern: /^\d{3}-\d{3}$/, message: 'Формат: 123-456'}
                    ]}
                >
                    <Input placeholder="123-456" maxLength={7}/>
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={isSubmitting}
                        style={{
                            backgroundColor: olympiad ? gradeColors[olympiad.grades] : '#1890ff',
                            borderColor: olympiad ? gradeColors[olympiad.grades] : '#1890ff'
                        }}
                    >
                        {isSubmitting ? 'Отправка...' : 'Подтвердить запись'}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default RegistrationModal;